import React from "react";
import { useEffect, useState } from "react";
import { BiRefresh, BiErrorAlt } from "react-icons/bi";
import EStatus from "../../enums/status";
import {
  ButtonsWrapper,
  CardContentWrapper,
  CardHeader,
  CardList,
  CardListItem,
  CardTitle,
  Container,
  ErrorMessage,
  ErrorRow,
  ErrorTitle,
  ErrorWrapper,
  Link,
} from "./styles";
import Button from "../../components/button";
import regulatorioService from "../../services/regulatorio-service";
import dashboardService from "../../services/dashboard-service";
import IEnvironmentStatus from "../../interfaces/environment-status";
import { CircularProgress } from "@mui/material";
import EStatusColors from "../../enums/status-colors";
import { IEnvironment } from "../../models/environment";
import CardSubmenu from "../card-submenu";
import IRoutineRequestTO from "../../interfaces/routine-request-to";
import { useConfirmModal } from "../../contexts/confirm-modal-context";

interface IProps {
  environment: IEnvironment;
  // eslint-disable-next-line no-unused-vars
  updateDashboardStatus(status: IEnvironmentStatus): void;
  initialDate: Date | null;
  finalDate: Date | null;
  isVisible: boolean | undefined;
}

const Card = ({ environment, updateDashboardStatus, initialDate, finalDate, isVisible }: IProps) => {

  const [environmentStatus, setEnvironmentStatus] = useState(
    {} as IEnvironmentStatus
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { showConfirmationDialog } = useConfirmModal();

  useEffect(() => {
    getRoutinesStatus(environment);
  }, []);

  async function getRoutinesStatus(environment: IEnvironment) {
    try {
      setLoading(true);
      setError("");

      const routineStatus = await dashboardService.getDashboardRoutines(
        environment, initialDate, finalDate
      );

      if (routineStatus) {
        setEnvironmentStatus(routineStatus);
        updateDashboardStatus({
          name: environment.name,
          ...routineStatus,
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  function getStatusBackgroundColor(status: EStatus): string {
    switch (status) {
      case EStatus.SUCESSO:
        return EStatusColors.SUCESSO;
      case EStatus.PROCESSANDO:
        return EStatusColors.PROCESSANDO;
      case EStatus.ERRO:
        return EStatusColors.ERRO;
      case EStatus.BLOQUEADA:
        return EStatusColors.BLOQUEADA;
      case EStatus.ALERTA:
        return EStatusColors.ALERTA;
      case EStatus.PENDENTE:
        return EStatusColors.PENDENTE;
    }
  }

  function getDateYesterday(): Date {
    const actualDate = new Date();
    const daysToMilliseconds = 86400000;
    return new Date(actualDate.getTime() - daysToMilliseconds);
  }

  async function handleRecap() {

    const accepted = await showConfirmationDialog(
      `Deseja executar a repescagem do dia ${getDateYesterday().toLocaleString().split(",")[0]} para ${environment.name}?`);
    if (!accepted) {
      return;
    }

    const routine: IRoutineRequestTO = {
      nome: "Repescagem Rotina ",
      tipo: "REPESCAGEM_ROTINA",
      prioridade: "IMEDIATA",
      dataProcessamento: new Date(),
      dataReferencia: getDateYesterday(),
    };

    try {
      setLoading(true);

      await regulatorioService.recapRoutine(environment, routine);

      await regulatorioService.recapImmediate(environment);

      await getRoutinesStatus(environment);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleExecuteScheduled() {

    const accepted = await showConfirmationDialog(`Deseja executar as rotinas AGENDADAS do ambiente ${environment.name}?`);
    if (!accepted) {
      return;
    }

    try {
      setLoading(true);

      await regulatorioService.recapScheduled(environment);

      await getRoutinesStatus(environment);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleExecuteImmediate() {

    const accepted = await showConfirmationDialog(`Deseja executar as rotinas IMEDIATAS do ambiente ${environment.name}?`);
    if (!accepted) {
      return;
    }

    try {
      setLoading(true);

      await regulatorioService.recapImmediate(environment);

      await getRoutinesStatus(environment);
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container isVisible={!!isVisible}>
      <CardHeader>
        <CardTitle>{environment.name}</CardTitle>
        <ButtonsWrapper>
          <Button
            onClick={() => getRoutinesStatus(environment)}
            disabled={loading}
          >
            <BiRefresh size={22} color="#000000de" />
          </Button>
          <CardSubmenu recap={handleRecap} disabled={loading}
            executeScheduled={handleExecuteScheduled}
            executeImmediate={handleExecuteImmediate} />
        </ButtonsWrapper>
      </CardHeader>
      <CardContentWrapper>
        <Link target="_blank" rel="noreferrer" href={environment.baseUrl}>
          {environment.baseUrl}
        </Link>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "15px 0 10px",
            }}
          >
            <CircularProgress size={24} value={100} />
          </div>
        ) : error ? (
          <ErrorWrapper>
            <ErrorRow>
              <ErrorTitle>
                Não foi possível recuperar as informações do ambiente:
              </ErrorTitle>
              <ErrorMessage>{error}</ErrorMessage>
            </ErrorRow>
            <BiErrorAlt size={22} />
          </ErrorWrapper>
        ) : (
          <>
            <CardList>
              <CardListItem
                bgColor={
                  environmentStatus.erro > 0
                    ? getStatusBackgroundColor(EStatus.ERRO)
                    : getStatusBackgroundColor(EStatus.SUCESSO)
                }
              >
                {environmentStatus.erro}
                <span>
                  Erro
                  <div />
                </span>
              </CardListItem>
              <CardListItem
                bgColor={
                  environmentStatus.alerta > 0
                    ? getStatusBackgroundColor(EStatus.ALERTA)
                    : getStatusBackgroundColor(EStatus.SUCESSO)
                }
              >
                {environmentStatus.alerta}
                <span>
                  Alerta
                  <div />
                </span>
              </CardListItem>
              <CardListItem
                bgColor={
                  environmentStatus.bloqueada > 0
                    ? getStatusBackgroundColor(EStatus.BLOQUEADA)
                    : getStatusBackgroundColor(EStatus.SUCESSO)
                }
              >
                {environmentStatus.bloqueada}
                <span>
                  Bloqueada
                  <div />
                </span>
              </CardListItem>
              <CardListItem
                bgColor={
                  environmentStatus.processando > 0
                    ? getStatusBackgroundColor(EStatus.PROCESSANDO)
                    : getStatusBackgroundColor(EStatus.SUCESSO)
                }
              >
                {environmentStatus.processando}
                <span>
                  Processando
                  <div />
                </span>
              </CardListItem>
              <CardListItem
                bgColor={
                  environmentStatus.pendente > 0
                    ? getStatusBackgroundColor(EStatus.PENDENTE)
                    : getStatusBackgroundColor(EStatus.SUCESSO)
                }
              >
                {environmentStatus.pendente}
                <span>
                  Pendente
                  <div />
                </span>
              </CardListItem>
            </CardList>
          </>
        )}
      </CardContentWrapper>
    </Container>
  );
};

export default Card;
